/**
 * Client-safe allowlist of the entity table-search keys whose `/api/search/{entity}`
 * route is backed by a `search_<entity>_hybrid` RPC (query embedding + FTS). These are
 * the ONLY entities for which the client requests `?mode=hybrid`; every other searchable
 * entity (tickets, time_entries, offerings, …) stays keyword-only.
 *
 * MUST stay free of server imports (no `query-embedding`/`search-mode`, which pull
 * Bedrock + `pg` in): this module is imported by client hooks, so a server import here
 * would break the client bundle (see AURORA_CACHE_AVAILABILITY).
 */

/** Table-search entity keys that support hybrid (semantic) search on the server. */
export const HYBRID_SEARCH_ENTITY_TYPES = [
  "contacts",
  "companies",
  "deals",
  "tasks",
  "communications",
] as const;

export type HybridSearchEntityType = (typeof HYBRID_SEARCH_ENTITY_TYPES)[number];

/** Client-facing search mode for the FTS route (`semantic` is server-only, not used here). */
export type EntitySearchMode = "keyword" | "hybrid";

/** True when an entity's table search should call the route in hybrid (semantic) mode. */
export function isHybridSearchEntityType(
  entityType: string
): entityType is HybridSearchEntityType {
  return (HYBRID_SEARCH_ENTITY_TYPES as readonly string[]).includes(entityType);
}

/** Pick the search mode for an entity's table search: hybrid for embedded entities, else keyword. */
export function searchModeForEntity(entityType: string): EntitySearchMode {
  return isHybridSearchEntityType(entityType) ? "hybrid" : "keyword";
}
